import { Branch } from "./Branch";
import ProjectOverview from "./ProjectOverview";
import { IresDevice } from "./IresDevice";
import {
  hasClientEnabledStrictBranchFilters,
  getBranchesOfClient,
} from "../repository/ClientRepository";
import { DeviceOverview } from "./DeviceOverview";
import ClientPanel from "./ClientPanel";
import { isClientPanel } from "./DeviceSettingsManager";
import { getDefaultBranchFilterOfUser } from "../repository/UserRepository";
import { compareArrays, compareSets } from "@/utils/Utils";

export class BranchFilter {
  branchIds: Set<string> = new Set();
  branches: Branch[] = [];
  isSingleBranchFilter: boolean;
  isStrict = true;
  doesSeeAllBranches = false;

  get branch() {
    return this.isSingleBranchFilter ? this.branches[0] : null;
  }

  constructor(strict: boolean, ...branches: Branch[]) {
    if (branches.length == 1) {
      const branch = branches[0];
      this.isSingleBranchFilter = true;
      this.branches = [branch];
      this.branchIds.add(branch.id);
      
    } else {
      this.isSingleBranchFilter = false;

      branches.forEach((branch) => {
        this.branches.push(branch);
        this.branchIds.add(branch.id);
      });

      this.doesSeeAllBranches =
        getBranchesOfClient().length === this.branches.length;
    }

    if (strict !== undefined) {
      this.isStrict = strict;
    } else {
      this.isStrict = hasClientEnabledStrictBranchFilters();
    }
  }

  /**
   * Checks if the current branch filter instance matches the default branch filter for a user.
   * This method uses utility functions to perform deep comparisons on complex properties.
   * 
   * @returns {boolean} True if the current instance matches the default branch filter, false otherwise.
   */
  isDefaultBranchFilter(): boolean {
    // Retrieve the default branch filter for the user. It returns an instance of BranchFilter set to 
    // the user's default filter settings.
    const defaultBranchFilterOfUser = getDefaultBranchFilterOfUser();
    
    // Compare the branchIds sets of the current instance and the default filter. If they don't match,
    // return false immediately. This uses the compareSets utility function to check for equality.
    if (!compareSets(this.branchIds, defaultBranchFilterOfUser.branchIds)) return false;

    // Compare the branches arrays of the current instance and the default filter. If they don't match,
    // return false immediately. This step involves a deep comparison of each branch object within the arrays
    // using the compareArrays utility function.
    if (!compareArrays(this.branches, defaultBranchFilterOfUser.branches)) return false;

    // Compare the isSingleBranchFilter boolean values. If they differ, the filters are not considered identical,
    // hence return false.
    if (this.isSingleBranchFilter !== defaultBranchFilterOfUser.isSingleBranchFilter) return false;

    // Finally, compare the doesSeeAllBranches boolean values. Any difference indicates the filters do not match,
    // leading to a return value of false.
    if (this.doesSeeAllBranches !== defaultBranchFilterOfUser.doesSeeAllBranches) return false;

    // If none of the above checks resulted in a return of false, it means all properties match. Therefore,
    // the current instance is considered to match the default branch filter, and true is returned.
    return true;
  }

  doesMatchIresDevice(iresDevice: IresDevice) {
    const assignment = iresDevice.currentAssignment;

    // Only use the project branch if the project assignment started on or after the
    // device was assigned to this client. Stale project data from a previous client
    // must not influence branch filtering.
    const isProjectFromCurrentClient = assignment?.currentProjectId
      && assignment.currentProjectAssignmentTimeRange?.startDate
      && assignment.startDate
      && assignment.currentProjectAssignmentTimeRange.startDate.toMillis() >= assignment.startDate.toMillis();

    const branchNumber = isProjectFromCurrentClient
      ? (assignment?.currentProjectBranch ?? assignment?.deviceBranch)
      : assignment?.deviceBranch;

    return this.doesMatchBranchNumber(branchNumber);
  }

  doesMatchGenericDevice(device: DeviceOverview | ClientPanel, forceStrictCheck = true) {
    let branchNumber: string | null = null;

    // Both device kinds prefer the project's branch over the device's own Standort,
    // so a device deployed to a project in another branch still matches that
    // branch's filter while `branch` keeps pointing at where the device lives.
    // DeviceOverview had no such fallback, which is why the Cloud generic-device
    // assignment path still overwrites `branch` (BUG-010 in the Cloud repo). This
    // is the prerequisite for that being fixed: until Cloud writes
    // currentProjectBranch, it is absent and the behaviour is unchanged.
    if (isClientPanel(device)) {
      branchNumber = device.currentProjectBranch ?? device.branch;
    }
    else {
      branchNumber = device.currentProjectBranch ?? device.branch ?? null;
    }

    return this.doesMatchBranchNumber(branchNumber, forceStrictCheck);
  }

  doesMatchBranchNumber(branchNumber: string | null | undefined, forceStrictCheck?: boolean) {
    if ((this.isStrict && this.isSingleBranchFilter) || forceStrictCheck) {
      return this.branches.some(
        (branch) => branch.branchNumber.toString() == branchNumber
      );
    }

    return (
      !branchNumber ||
      branchNumber == "0" ||
      this.branches.length === 0 ||
      this.branches.some(
        (branch) => branch.branchNumber.toString() == branchNumber
      )
    );
  }

  doesProjectOverviewMatchFilter(projectOverview: ProjectOverview) {
    const branchIds = this.branchIds;

    if (this.isSingleBranchFilter && this.isStrict) {
      if (branchIds.has(projectOverview.branch ?? "") || !projectOverview.branch) {
        return true;
      }
      return "";
    }

    return (
      branchIds.size == 0 ||
      this.doesSeeAllBranches ||
      !projectOverview.branch ||
      branchIds.has(projectOverview.branch)
    );
  }
}
